import type { HttpInterceptorFn } from '@angular/common/http'
import { HttpErrorResponse } from '@angular/common/http'

import { tap } from 'rxjs'

import { httpFailureCause } from '../shared/utils/http-error.util'

/**
 * Logs why a request never reached the server.
 *
 * `status: 0` means `fetch` itself rejected - DNS failure, TLS problem,
 * connection refused - and `HttpErrorResponse.error` carries that reason. This
 * is the last point at which it still exists: apollo-angular's HttpLink turns
 * anything below status 300 into `new Error(err.message)`
 * (`convertHttpErrorToApolloError`), discarding the cause, so every GraphQL
 * consumer downstream can only ever see the contentless `Http failure response
 * for <url>: 0 undefined`. That is unusable for telling apart an unreachable
 * API, a DNS problem and a rejected certificate - which is exactly what you
 * need to know when SSR fails only inside the cluster.
 */
export const httpFailureLoggingInterceptor: HttpInterceptorFn = (req, next) =>
  next(req).pipe(
    tap({
      error: (error: unknown) => {
        if (error instanceof HttpErrorResponse && error.status === 0) {
          console.error(
            `Request to ${req.url} never reached the server:`,
            httpFailureCause(error)
          )
        }
      },
    })
  )
